import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { s3baseUrl } from "src/config/config";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrMediaCard from "src/design-system/components/GrMediaCard";
import GrBadge from "src/design-system/components/GrBadge";
import GrButton from "src/design-system/components/GrButton";
import GrIcon from "src/design-system/components/GrIcon";
import GrVideoDialog from "src/design-system/components/GrVideoDialog";
import { formatDay } from "../Programmes/programmeHelpers";
import { isFirstRow, isVimeoShareLink, libraryGridSx, videoType } from "./videoHelpers";
import { vimeo_share_embed_api } from "src/DAL/Programmes/Programmes";

/**
 * Videos of one programme as cards (type on the image, date, completed). The card or Watch opens
 * the video in the player popup, which repeats the type, date and completed.
 */

const badgeOnImageSx = { background: gr.color.surface };
const metaSx = { display: "flex", flexWrap: "wrap", alignItems: "center", gap: gr.space[2] };
const metaItemSx = { display: "inline-flex", alignItems: "center", gap: gr.space[1] };
const dotSx = { color: gr.color.lineStrong };
const doneSx = { ...metaItemSx, color: gr.color.ok, fontWeight: gr.font.weight.medium };

const TypeBadge = ({ video, sx }) => {
  const type = videoType(video);
  return (
    <GrBadge sx={sx} icon={<GrIcon icon={type.icon} size="sm" />}>
      {type.label}
    </GrBadge>
  );
};

const VideoMeta = ({ video }) => (
  <Box sx={metaSx}>
    <Box component="span" sx={metaItemSx}>
      <GrIcon icon={icons.calendar} size="sm" />
      {formatDay(video?.workout_date)}
    </Box>
    {video?.workout_completed ? (
      <>
        <Box component="span" aria-hidden="true" sx={dotSx}>
          ·
        </Box>
        <Box component="span" sx={doneSx}>
          <GrIcon icon={icons.checkCircle} size="sm" />
          Completed
        </Box>
      </>
    ) : null}
  </Box>
);

const VideoLibraryCard = ({ videos }) => {
  const [playing, setPlaying] = useState(null);
  const close = () => setPlaying(null);

  // Vimeo share links (vimeo.com/share/…) can't be played as they are: the API looks up their
  // player link once per link; the popup shows a spinner meanwhile. Other links play as before.
  const [shareLinks, setShareLinks] = useState({}); // share link -> player link ("" = not found)
  const shareUrl = playing && isVimeoShareLink(playing.video_url) ? playing.video_url.trim() : "";
  const shareKnown = Boolean(shareUrl) && shareUrl in shareLinks;
  useEffect(() => {
    if (!shareUrl || shareKnown) return undefined;
    let alive = true;
    const remember = (link) => {
      if (alive) setShareLinks((map) => ({ ...map, [shareUrl]: link }));
    };
    vimeo_share_embed_api(shareUrl)
      .then((res) => remember(res?.code === 200 && res.embed_url ? res.embed_url : ""))
      .catch(() => remember(""));
    return () => {
      alive = false;
    };
  }, [shareUrl, shareKnown]);
  // Not found → the original link, as before.
  const playUrl = shareUrl ? (shareKnown ? shareLinks[shareUrl] || playing.video_url : undefined) : playing?.video_url;

  return (
    <>
      <Box sx={libraryGridSx}>
        {videos.map((video, index) => {
          const thumb = video?.video_thumbnail?.thumbnail_1;
          return (
            <GrMediaCard
              eagerImage={isFirstRow(index)}
              key={video?._id || index}
              image={thumb ? s3baseUrl + thumb : null}
              imageAlt={video?.title}
              placeholderIcon={icons.video}
              play
              badge={<TypeBadge video={video} sx={badgeOnImageSx} />}
              title={video?.title || "Video"}
              meta={<VideoMeta video={video} />}
              onClick={() => setPlaying(video)}
              actions={
                <GrButton
                  variant="soft"
                  size="sm"
                  startIcon={<GrIcon icon={icons.playArrow} size="sm" />}
                  sx={{ flex: "1 1 0" }}
                  onClick={() => setPlaying(video)}
                >
                  Watch
                </GrButton>
              }
            />
          );
        })}
      </Box>

      <GrVideoDialog
        open={Boolean(playing)}
        onClose={close}
        onEnded={close}
        url={playUrl}
        loading={Boolean(shareUrl) && !shareKnown}
        title={playing?.title || "Video"}
        badge={playing ? <TypeBadge video={playing} /> : null}
        meta={playing ? <VideoMeta video={playing} /> : null}
      />
    </>
  );
};

export default VideoLibraryCard;
